import { useState } from "react";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/base/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/base/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/base/tooltip";
import { ReferralsRepository } from "@/application/referrals/referrals_repository";
import type {
  ReplayReferralFlowResult,
  ReplayStepResult,
} from "@/application/referrals/types";
import { StageBadge } from "./StageBadge";
import {
  parseTracks,
  type MerchantLifecycle,
  type UserProfile,
} from "../types";

const repo = new ReferralsRepository();

const REFERRAL_TRACK_NAME = "referral";

const TERMINAL_STAGES = new Set(["eligible", "rewarded", "ineligible"]);

const STEP_LABELS: Record<ReplayStepResult["name"], string> = {
  signup: "Signup",
  identity_condition: "IDV condition",
  ttp_condition: "Tap-to-Pay condition",
  payout: "Reward payout",
};

interface Props {
  businessId: string;
  primaryUser: UserProfile | null;
  lifecycle: MerchantLifecycle | null;
  onRefresh: () => void;
}

export function ReferralCard({
  businessId,
  primaryUser,
  lifecycle,
  onRefresh,
}: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ReplayReferralFlowResult | null>(null);

  const referralCode = primaryUser?.referralCode ?? null;
  const referrerName = primaryUser?.referrerName ?? null;

  const tracks = lifecycle ? parseTracks(lifecycle.tracks) : {};
  // eslint-disable-next-line security/detect-object-injection -- closed constant key
  const referralTrack = tracks[REFERRAL_TRACK_NAME];
  const referralStage = referralTrack?.stage ?? "not_referred";
  const payoutStatus =
    (referralTrack?.metadata?.payout as { status?: string } | undefined)
      ?.status ?? null;
  const hasPayoutError = payoutStatus === "failed";

  if (!referralCode) {
    return <p className="text-subtle-foreground text-sm">Not referred</p>;
  }

  const canReplay = !TERMINAL_STAGES.has(referralStage);
  const disabledReason = canReplay
    ? null
    : `Lifecycle is at "${referralStage}" — replay is disabled for eligible/rewarded/ineligible tracks`;

  async function handleReplay() {
    setSubmitting(true);
    try {
      const res = await repo.replayReferralFlow(businessId);
      setResult(res);
      if (!res.success) {
        toast.error(res.error ?? "Replay failed");
      } else {
        toast.success(`Replay complete — final stage: ${res.finalStage}`);
      }
      onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Replay failed");
    } finally {
      setSubmitting(false);
    }
  }

  function closeDialog() {
    setConfirmOpen(false);
    setResult(null);
  }

  return (
    <div>
      <div className="space-y-3">
        <div>
          <p className="text-muted-foreground text-sm">Referrer name</p>
          <p className="text-foreground text-sm">{referrerName ?? "—"}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-sm">Referral code (UCC)</p>
          <p className="text-foreground font-mono text-sm">{referralCode}</p>
        </div>
        <div>
          <p className="text-muted-foreground mb-1 text-sm">Track stage</p>
          <StageBadge stage={referralStage} hasError={hasPayoutError} />
        </div>

        <div className="pt-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-block">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canReplay}
                    onClick={() => {
                      setResult(null);
                      setConfirmOpen(true);
                    }}
                  >
                    Replay referral flow
                  </Button>
                </span>
              </TooltipTrigger>
              {disabledReason && (
                <TooltipContent>{disabledReason}</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog();
          else setConfirmOpen(true);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replay referral flow?</DialogTitle>
            <DialogDescription>
              This replays the full referral flow (signup, IDV condition,
              Tap-to-Pay condition). If the user qualifies, Cello attribution
              events will be fired and the referrer becomes eligible for payout.
              Proceed?
            </DialogDescription>
          </DialogHeader>

          {result && (
            <div className="border-border bg-muted space-y-2 rounded-md border p-3 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-foreground font-semibold">
                  Final stage
                </span>
                <StageBadge stage={result.finalStage ?? "not_referred"} />
              </div>
              <ul className="space-y-2">
                {result.steps.map((s) => (
                  <li
                    key={s.name}
                    className="border-border bg-card rounded border p-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-foreground font-semibold">
                        {STEP_LABELS[s.name]}
                      </span>
                      {s.transitioned ? (
                        <span className="text-foreground font-mono">
                          {s.previousStage} → {s.newStage}
                        </span>
                      ) : s.skippedReason ? (
                        <span className="text-muted-foreground">
                          skipped: {s.skippedReason}
                        </span>
                      ) : s.error ? (
                        <span className="text-red-600">error</span>
                      ) : (
                        <span className="text-muted-foreground">no-op</span>
                      )}
                    </div>
                    {s.error && <p className="mt-1 text-red-600">{s.error}</p>}
                    {s.celloReport && (
                      <p className="text-muted-foreground mt-1">
                        Cello {s.celloReport.type}:{" "}
                        {s.celloReport.success ? (
                          <span className="text-emerald-700">reported</span>
                        ) : (
                          <span className="text-red-600">
                            failed
                            {s.celloReport.error
                              ? ` — ${s.celloReport.error}`
                              : ""}
                          </span>
                        )}
                      </p>
                    )}
                    {s.payoutReport && (
                      <p className="text-muted-foreground mt-1">
                        Payout:{" "}
                        {s.payoutReport.status === "succeeded" ? (
                          <span className="text-emerald-700">
                            succeeded (attempt {s.payoutReport.attemptCount})
                          </span>
                        ) : s.payoutReport.status === "failed" ? (
                          <span className="text-red-600">
                            failed
                            {s.payoutReport.lastError
                              ? ` — ${s.payoutReport.lastError}`
                              : ""}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            {s.payoutReport.status ?? "no attempt"}
                          </span>
                        )}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeDialog}
              disabled={submitting}
            >
              {result ? "Close" : "Cancel"}
            </Button>
            {!result && (
              <Button onClick={handleReplay} disabled={submitting}>
                {submitting && (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                )}
                Replay
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
